import { EmployerDto } from '@zorro/clients';
import { Select, type SelectProps } from '@zorro/design-system/v2/Select';
import { SubEntityVariant } from '@zorro/types';
import { FormErrorMessage } from '@zorro/web/design-system';
import { useMonolithQuery } from '@zorro/web/ui-utils';
import {
  FieldPath,
  FieldValues,
  UseControllerProps,
  useController,
} from 'react-hook-form';

type Props<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = UseControllerProps<TFieldValues, TName> & {
  employerId: EmployerDto['id'];
  selectProps?: SelectProps;
  type?: SubEntityVariant;
};

export const EmployeeSubEntityInput = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  employerId,
  selectProps,
  type = SubEntityVariant.subEntity,
  ...formField
}: Props<TFieldValues, TName>) => {
  const {
    field,
    formState: { errors },
  } = useController(formField);

  const subEntityLabels = {
    [SubEntityVariant.subEntity]: 'Sub-entity 1',
    [SubEntityVariant.subEntity2]: 'Sub-entity 2',
  };

  const method =
    type === SubEntityVariant.subEntity
      ? 'subEntityControllerFindMany'
      : 'subEntity2ControllerFindMany';

  const label = subEntityLabels[type];
  const placeholder =
    type === SubEntityVariant.subEntity
      ? 'Select a sub-entity 1'
      : 'Select a sub-entity 2';

  const { data: subEntities = [] } = useMonolithQuery({
    method,
    params: [employerId],
  });

  return (
    <>
      <Select
        {...field}
        {...selectProps}
        label={label}
        placeholder={placeholder}
        searchable
        data={subEntities.map((subEntity) => ({
          value: subEntity.id,
          label: subEntity.name,
        }))}
      />

      <FormErrorMessage fieldName={formField.name} errors={errors} />
    </>
  );
};
